import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}

const CODE_RE = /^[A-Za-z0-9_-]{2,32}$/;

export function NewAffiliateDialog({ open, onOpenChange, onCreated }: Props) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pct, setPct] = useState('20');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  function reset() {
    setName(''); setEmail(''); setCode(''); setPct('20'); setNotes('');
  }

  async function handleSave() {
    const trimmedCode = code.trim();
    const pctNum = Number(pct);
    if (!name.trim() || !email.trim() || !trimmedCode) {
      toast({ title: 'Missing fields', description: 'Name, email, and code are required.', variant: 'destructive' });
      return;
    }
    if (!CODE_RE.test(trimmedCode)) {
      toast({ title: 'Invalid code', description: 'Use 2–32 letters, digits, dash or underscore.', variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 100) {
      toast({ title: 'Invalid commission %', description: 'Enter a number between 0 and 100.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    const { error } = await supabase
      .from('affiliates')
      .insert({
        name: name.trim(),
        email: email.trim(),
        code: trimmedCode,
        commission_pct: pctNum,
        notes: notes.trim() || null,
      });
    setSaving(false);

    if (error) {
      toast({
        title: 'Could not create affiliate',
        description: error.message.includes('affiliates_code_key') ? 'That code is already in use.' : error.message,
        variant: 'destructive',
      });
      return;
    }

    toast({ title: 'Affiliate created' });
    reset();
    onOpenChange(false);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New affiliate</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="aff-name">Name</Label>
            <Input id="aff-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="aff-email">Email</Label>
            <Input id="aff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="aff-code">Referral code</Label>
            <Input id="aff-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="JANE25" />
            <p className="text-xs text-muted-foreground">Used in <code>?ref=CODE</code>. Letters, digits, dash, underscore.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="aff-pct">Commission %</Label>
            <Input id="aff-pct" type="number" min="0" max="100" step="0.01" value={pct} onChange={(e) => setPct(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="aff-notes">Notes (optional)</Label>
            <Textarea id="aff-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
